import { ReactNode, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DefaultValues,
  FieldValues,
  SubmitHandler,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { z } from "zod";

import { EditIcon } from "./icons";
import { CancelSubmitButtons, CancelSubmitButtonsProps } from "./ui";

interface RenderFieldProps<T extends FieldValues> extends UseFormReturn<T> {
  isEditing: boolean;
}

interface SingleFieldFormCardProps<T extends FieldValues> extends Pick<
  CancelSubmitButtonsProps,
  "submitButtonLabel" | "cancelButtonLabel"
> {
  title: string;
  validationSchema: z.ZodType<T>;
  defaultValues?: DefaultValues<T>;
  onSubmit: SubmitHandler<T>;
  renderField: (props: RenderFieldProps<T>) => ReactNode;
}

export const SingleFieldFormCard = <T extends FieldValues>({
  title,
  submitButtonLabel,
  cancelButtonLabel,
  validationSchema,
  defaultValues,
  onSubmit,
  renderField,
}: SingleFieldFormCardProps<T>) => {
  const [isEditing, setIsEditing] = useState(false);

  const singleFieldForm = useForm({
    resolver: zodResolver(validationSchema as never),
    defaultValues,
  }) as UseFormReturn<T>;

  const submitAndClose: SubmitHandler<T> = async (data) => {
    try {
      await onSubmit(data);
      setIsEditing(false);
    } catch (_e) {
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    singleFieldForm.reset();
  };

  return (
    <form
      onSubmit={singleFieldForm.handleSubmit(submitAndClose)}
      className="flex flex-col gap-3 rounded-lg border px-5 py-6 shadow-inner shadow-gray-50 transition-all duration-700"
    >
      <div className="flex flex-row items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        {isEditing ? (
          <CancelSubmitButtons
            containerClassName="w-1/4"
            variant="right-outline-black"
            handleCancel={handleCancel}
            cancelButtonLabel={cancelButtonLabel}
            submitButtonLabel={submitButtonLabel}
          />
        ) : (
          <button onClick={() => setIsEditing(true)}>
            <EditIcon />
          </button>
        )}
      </div>

      <div>{renderField({ isEditing, ...singleFieldForm })}</div>
    </form>
  );
};
